import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, BarChart3, Users, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { caseStudies } from '../data/caseStudies';
import { TienCharacter } from '../components/TienCharacter';
export function Home() {
  const featuredWork = caseStudies.filter(s => ['copilot-voice', 'copilot-chat-growth', 'copilot-chat-launch'].includes(s.id));
  const timeline = [{
    year: '1998',
    event: 'Born in Pleiku, Vietnam'
  }, {
    year: '1999',
    event: 'Moved to live with my grandma'
  }, {
    year: '2016',
    event: ['Got a full-ride scholarship at Babson College', 'B.S of Science in Business Analytics & Information Technology Management']
  }, {
    year: '2018',
    event: ['Founded SheCodes Vietnam NGO', 'Raised funds; reached 10K followers in 1 month with 0 marketing budget', 'Hosted the first female hackathon in Southeast Asia']
  }, {
    year: '2019',
    event: ['Took 2 years off college to work', 'PM @Shopee, e-commerce startup in Singapore']
  }, {
    year: '2022',
    event: 'Product Manager @ Microsoft'
  }, {
    year: 'Next',
    event: 'To be continued :)'
  }];
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };
  return <div className="max-w-5xl mx-auto px-6 pt-24 pb-12" data-id="element-148">
      {/* Hero Section */}
      <motion.section initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6,
      ease: 'easeOut'
    }} className="max-w-3xl mb-32" data-id="element-149">
        <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 text-balance" data-id="element-150">
          Hi, I'm Tien
        </h1>
        <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-2xl" data-id="element-151">
          I'm a Product Manager, who loves building things that empower people
          to do their best work, guided by user empathy and data.
        </p>
        <div className="flex items-center gap-4" data-id="element-152">
          <Link to="/work" className="px-6 py-3 bg-black text-white rounded-full font-medium hover:bg-gray-800 transition-colors flex items-center gap-2" data-id="element-153">
            View Work <ArrowRight className="w-4 h-4" data-id="element-154" />
          </Link>
        </div>
      </motion.section>

      {/* At the Moment & My Niche Cards */}
      <motion.section initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6,
      ease: 'easeOut',
      delay: 0.2
    }} className="relative grid grid-cols-1 md:grid-cols-2 gap-6 mb-32" data-id="element-155">
        <TienCharacter data-id="element-156" />
        <div className="p-8 rounded-2xl border border-gray-200 bg-white shadow-sm relative z-10" data-id="element-157">
          <h2 className="text-sm font-bold tracking-widest uppercase mb-4 text-black" data-id="element-158">
            At the Moment
          </h2>
          <p className="text-gray-600 leading-relaxed" data-id="element-159">
            I'm a Product Manager at Microsoft AI, building Voice & Chat
            experience in Edge Copilot, and training jiu-jitsu on the side.
          </p>
        </div>

        <div className="p-8 rounded-2xl border border-gray-200 bg-white shadow-sm relative z-10" data-id="element-160">
          <h2 className="text-sm font-bold tracking-widest uppercase mb-5 text-black" data-id="element-161">
            My Niche
          </h2>
          <div className="flex flex-wrap gap-3" data-id="element-162">
            {['Voice UX', 'LLM Evals', 'AI Products', 'Prompt Engineering', 'A/B Testing', '0-to-1 Execution', 'Growth Strategy', 'Consumer Apps'].map(tag => <span key={tag} className="px-3 py-1 bg-gray-100 text-sm font-medium rounded-full text-gray-800" data-id="element-163">
                {tag}
              </span>)}
          </div>
        </div>
      </motion.section>

      {/* Highlights Section */}
      <motion.section variants={container} initial="hidden" whileInView="show" viewport={{
      once: true,
      margin: '-100px'
    }} className="mb-32" data-id="element-164">
        <h2 className="text-3xl font-bold tracking-tight mb-8 text-center" data-id="element-165">
          Career Highlights
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-166">
          <motion.div variants={item} className="p-8 border border-gray-200 rounded-2xl bg-white shadow-md hover:shadow-lg transition-shadow duration-300" data-id="element-167">
            <BarChart3 className="w-6 h-6 mb-4 text-black" data-id="element-168" />
            <div className="text-3xl font-bold mb-2" data-id="element-169">2x</div>
            <div className="text-sm text-gray-600 font-medium mb-2" data-id="element-170">
              D7 Retention
            </div>
            <p className="text-sm text-gray-500" data-id="element-171">
              Doubled retention for Edge Vision
            </p>
          </motion.div>

          <motion.div variants={item} className="p-8 border border-gray-200 rounded-2xl bg-white shadow-md hover:shadow-lg transition-shadow duration-300" data-id="element-172">
            <Zap className="w-6 h-6 mb-4 text-black" data-id="element-173" />
            <div className="text-3xl font-bold mb-2" data-id="element-174">1M+</div>
            <div className="text-sm text-gray-600 font-medium mb-2" data-id="element-175">
              Incremental WAU
            </div>
            <p className="text-sm text-gray-500" data-id="element-176">
              Drove growth experiments for Copilot in Edge
            </p>
          </motion.div>

          <motion.div variants={item} className="p-8 border border-gray-200 rounded-2xl bg-white shadow-md hover:shadow-lg transition-shadow duration-300" data-id="element-177">
            <Users className="w-6 h-6 mb-4 text-black" data-id="element-178" />
            <div className="text-3xl font-bold mb-2" data-id="element-179">10K+</div>
            <div className="text-sm text-gray-600 font-medium mb-2" data-id="element-180">
              Community Members
            </div>
            <p className="text-sm text-gray-500" data-id="element-181">
              Founded SheCodes Vietnam, recognized by UN Women.
            </p>
          </motion.div>
        </div>
      </motion.section>

      {/* Decorated sections wrapper - My Journey through Featured Work */}
      <div className="relative overflow-hidden" data-id="element-182">
        {/* Floating decorative elements */}
        {[{
        top: '2%',
        left: '-3%',
        size: 28,
        color: '#93c5fd',
        opacity: 0.5,
        duration: 10,
        delay: 0
      }, {
        top: '8%',
        right: '-2%',
        size: 20,
        color: '#5eead4',
        opacity: 0.6,
        duration: 12,
        delay: 1
      }, {
        top: '14%',
        left: '2%',
        size: 14,
        color: '#c4b5fd',
        opacity: 0.45,
        duration: 9,
        delay: 2.5
      }, {
        top: '20%',
        right: '3%',
        size: 36,
        color: '#a5b4fc',
        opacity: 0.4,
        duration: 14,
        delay: 0.5
      }, {
        top: '28%',
        left: '-1%',
        size: 22,
        color: '#5eead4',
        opacity: 0.55,
        duration: 11,
        delay: 3
      }, {
        top: '35%',
        right: '-1%',
        size: 16,
        color: '#bae6fd',
        opacity: 0.5,
        duration: 8,
        delay: 1.5
      }, {
        top: '42%',
        left: '4%',
        size: 40,
        color: '#93c5fd',
        opacity: 0.35,
        duration: 13,
        delay: 4
      }, {
        top: '50%',
        right: '2%',
        size: 18,
        color: '#c4b5fd',
        opacity: 0.5,
        duration: 10,
        delay: 2
      }, {
        top: '58%',
        left: '1%',
        size: 24,
        color: '#a5b4fc',
        opacity: 0.45,
        duration: 12,
        delay: 0.8
      }, {
        top: '65%',
        right: '4%',
        size: 32,
        color: '#5eead4',
        opacity: 0.4,
        duration: 15,
        delay: 3.5
      }, {
        top: '72%',
        left: '-2%',
        size: 16,
        color: '#bae6fd',
        opacity: 0.55,
        duration: 9,
        delay: 1.2
      }, {
        top: '80%',
        right: '-3%',
        size: 26,
        color: '#93c5fd',
        opacity: 0.5,
        duration: 11,
        delay: 4.5
      }, {
        top: '88%',
        left: '3%',
        size: 20,
        color: '#c4b5fd',
        opacity: 0.4,
        duration: 13,
        delay: 2.8
      }, {
        top: '94%',
        right: '1%',
        size: 14,
        color: '#5eead4',
        opacity: 0.6,
        duration: 10,
        delay: 0.3
      }].map((dot, i) => <motion.div key={`dot-${i}`} className="absolute pointer-events-none z-0" style={{
        top: dot.top,
        left: dot.left,
        right: dot.right,
        width: dot.size,
        height: dot.size
      }} animate={{
        y: [0, -12, 0, 8, 0],
        rotate: [0, 10, -5, 8, 0]
      }} transition={{
        duration: dot.duration,
        delay: dot.delay,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-183">
            <svg width={dot.size} height={dot.size} viewBox="0 0 40 40" fill="none" data-id="element-184">
              <ellipse cx="20" cy="10" rx="6" ry="10" fill={dot.color} opacity={dot.opacity} transform="rotate(0 20 20)" data-id="element-185" />
              <ellipse cx="20" cy="10" rx="6" ry="10" fill={dot.color} opacity={dot.opacity} transform="rotate(72 20 20)" data-id="element-186" />
              <ellipse cx="20" cy="10" rx="6" ry="10" fill={dot.color} opacity={dot.opacity} transform="rotate(144 20 20)" data-id="element-187" />
              <ellipse cx="20" cy="10" rx="6" ry="10" fill={dot.color} opacity={dot.opacity} transform="rotate(216 20 20)" data-id="element-188" />
              <ellipse cx="20" cy="10" rx="6" ry="10" fill={dot.color} opacity={dot.opacity} transform="rotate(288 20 20)" data-id="element-189" />
              <circle cx="20" cy="20" r="4" fill={dot.color} opacity={dot.opacity + 0.15} data-id="element-190" />
            </svg>
          </motion.div>)}

        {/* About Section */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.5
      }} className="mb-32 relative z-10" data-id="element-191">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center" data-id="element-192">
            <div className="md:col-span-5" data-id="element-193">
              <div className="aspect-[3/4] bg-gray-100 rounded-3xl overflow-hidden relative" data-id="element-194">
                <img src="https://cdn.magicpatterns.com/uploads/6R3FvtvHEN3GQT6X36tEEo/jiujitsuseattleopen.jpg" alt="Profile" className="w-full h-full object-cover object-[center_20%]" data-id="element-195" />
              </div>
            </div>
            <div className="md:col-span-7" data-id="element-196">
              <h2 className="text-3xl font-bold tracking-tight mb-6 text-black" data-id="element-197">
                About Me
              </h2>
              <div className="space-y-4 text-lg text-gray-600 leading-relaxed" data-id="element-198">
                <p data-id="element-199">
                  I thrive in 0-to-1 environment, taking ambiguous problems and
                  turning them into shipped, metric-driven solutions. My
                  approach is deeply user-centric.
                </p>
                <p data-id="element-200">
                  Outisde of work, I'm a Purple belt in Jiu-jitsu. I'm a firm
                  believer in getting 1% better every day. I pursue continuous
                  growth both in my career and through my passion as a
                  competitive martial artist.
                </p>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="mt-24 max-w-3xl mx-auto" data-id="element-201">
            <h3 className="text-3xl font-bold tracking-tight mb-12 text-center text-black" data-id="element-202">
              My Journey
            </h3>
            <div className="space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-200 before:to-transparent" data-id="element-203">
              {timeline.map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-204">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-gray-100 text-gray-500 group-hover:bg-black group-hover:text-white shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-sm transition-colors duration-300 z-10" data-id="element-205">
                    <div className="w-2 h-2 bg-current rounded-full" data-id="element-206" />
                  </div>
                  <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-6 rounded-2xl border border-gray-100 bg-white shadow-sm group-hover:border-gray-300 transition-colors" data-id="element-207">
                    <div className="flex items-center justify-between mb-2" data-id="element-208">
                      <span className="font-bold text-black" data-id="element-209">{item.year}</span>
                    </div>
                    <div className="text-gray-600 leading-relaxed text-sm" data-id="element-210">
                      {Array.isArray(item.event) ? <ul className="list-disc pl-4 space-y-1" data-id="element-211">
                          {item.event.map((e, i) => <li key={i} data-id="element-212">{e}</li>)}
                        </ul> : <p data-id="element-213">{item.event}</p>}
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </motion.section>

        {/* Featured Work */}
        <section className="relative z-10" data-id="element-214">
          <div className="flex items-center justify-between mb-12" data-id="element-215">
            <h2 className="text-3xl font-bold tracking-tight text-black" data-id="element-216">
              Featured Work
            </h2>
          </div>

          <div className="space-y-12" data-id="element-217">
            {featuredWork.map((study, index) => <motion.div key={study.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="block" data-id="element-218">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center p-8 rounded-3xl bg-white border border-gray-200 shadow-md" data-id="element-219">
                  <div className="md:col-span-7" data-id="element-220">
                    <div className="flex items-center gap-3 mb-3" data-id="element-221">
                      <h3 className="text-2xl font-bold" data-id="element-222">{study.title}</h3>
                      <span className="px-3 py-1 bg-gray-100 text-xs font-semibold uppercase tracking-wider text-gray-500 rounded-full" data-id="element-223">
                        Coming soon
                      </span>
                    </div>
                    <p className="text-gray-600 mb-6 text-lg" data-id="element-224">
                      {study.subtitle}
                    </p>
                    <div className="flex flex-wrap gap-4" data-id="element-225">
                      {study.metrics.map((metric, i) => <div key={i} className="flex flex-col" data-id="element-226">
                          <span className="text-xl font-bold" data-id="element-227">
                            {metric.value}
                          </span>
                          <span className="text-xs text-gray-500 uppercase tracking-wider font-medium" data-id="element-228">
                            {metric.label}
                          </span>
                        </div>)}
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </section>
      </div>

      {/* Personal Project Section */}
      <motion.section initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-100px'
    }} transition={{
      duration: 0.5
    }} className="mt-32" data-id="element-229">
        <div className="flex items-center justify-between mb-12" data-id="element-230">
          <h2 className="text-3xl font-bold tracking-tight text-black" data-id="element-231">
            Personal Project
          </h2>
        </div>

        <div className="p-12 rounded-3xl border border-gray-200 border-dashed bg-gray-50 flex items-center justify-center min-h-[200px]" data-id="element-232">
          <p className="text-xl text-gray-500 font-medium italic" data-id="element-233">
            Coming soon...
          </p>
        </div>
      </motion.section>

      {/* Let's Connect Section */}
      <motion.section initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-100px'
    }} transition={{
      duration: 0.6,
      ease: 'easeOut'
    }} className="mt-32 mb-16 text-center bg-gray-50 rounded-3xl p-12 md:p-24 border border-gray-100" data-id="element-234">
        <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6" data-id="element-235">
          Let's connect.
        </h2>
        <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto" data-id="element-236">
          I'm always open to discussing product management, AI/Voice UX, or
          opportunities to build impactful products together.
        </p>
        <a href="mailto:mptienmai98@gmail.com?subject=Let's Connect" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white rounded-full font-medium text-lg hover:bg-gray-800 transition-colors shadow-sm hover:shadow-md" data-id="element-237">
          Reach out <ArrowRight className="w-5 h-5" data-id="element-238" />
        </a>
      </motion.section>
    </div>;
}